import { Visibility, VisibilityOff } from "@mui/icons-material";
import {
  Box,
  Card,
  CircularProgress,
  FormControl,
  IconButton,
  Input,
  InputAdornment,
  LinearProgress,
} from "@mui/material";
import Modal from "@mui/material/Modal";
import Typography from "@mui/material/Typography";
import BarcodeGif from "assets/gif/Barcode.gif";
import AachiLogo from "assets/images/logos/aachi-logo.png";
import PalletScooter from "assets/images/illustrations/pallet-scooter.png";
import { memo, useContext, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import { UserContext } from "../../../../../../../App";
import {
  // cartOfferValidation,
  dataRetriveWhenPageRefresh,
  deleteItemThroughScanner,
  // flushCart,
  flushCartNew,
  validateManagerPin,
} from "../../../../../../../config/Services";
import { eventTrackerConstant } from "../../../../../../common/event-tracker/event-tracker-constants";
import { eventTracker } from "../../../../../../common/event-tracker/eventTracker";
import { useSnackbar } from "../../../../hooks/SnackbarProvider";
import useGetDevice from "../../../../hooks/useGetDevice";
import { addcoupon } from "../../../redux/Actions/couponActions";
import { resetStateWithoutTime } from "../../../redux/Actions/eventTracker";
import { getFullState } from "../../../redux/Reducers/eventTrackerReducer";
import { getBarcodeStat } from "../../../redux/Reducers/paginationReducer";
import StyledButton from "../../custom/StyledButton";
import StyledSelect from "../../custom/StyledSelect";
import CartItemTable from "../../cartItemTable";
import "./index.css";

/* ── Grocery empty-cart tips ─────────────────────────────── */
const GROCERY_TIPS = [
  { icon: "🛒", text: "Loyalty members spend 20% more per visit on average" },
  { icon: "🍎", text: "Fresh produce is the #1 reason shoppers choose a grocery store" },
  { icon: "📊", text: "End-cap displays generate 3× more sales than mid-aisle shelves" },
  { icon: "💡", text: "Shoppers decide 70% of purchases while inside the store" },
  { icon: "📦", text: "Bundled offers lift average basket size by 20–30%" },
  { icon: "⏰", text: "Peak hours are 11 am–1 pm and 5–7 pm on weekdays" },
  { icon: "🎯", text: "Impulse purchases account for up to 60% of grocery revenue" },
  { icon: "🏷️", text: "Clear price labels reduce checkout queries by 40%" },
];

/* ─────────────────────────────────────────────────────────────
   Delivery-agent Vespa animation — full-width transit
   Timing: 1 s drive-in → 2 s stop (drop message) → 1 s drive-out
   ───────────────────────────────────────────────────────────── */
const GroceryEmptyCart = () => {
  const [phase, setPhase]             = useState("pre");
  const [bouncing, setBouncing]       = useState(false);
  const [showMessage, setShowMessage] = useState(false);
  const [tipIdx, setTipIdx]           = useState(0);
  const [tipVisible, setTipVisible]   = useState(false);
  const [cycleKey, setCycleKey]       = useState(0);  /* increments to restart loop */

  useEffect(() => {
    /* Reset transient scooter state at the top of every cycle */
    setPhase("pre");
    setBouncing(false);

    const timers = [
      /* 60 ms  → scooter starts crossing from left (1 s transit) */
      setTimeout(() => setPhase("driving-in"), 60),
      /* 1 060  → scooter arrives at centre; bounce */
      setTimeout(() => {
        setPhase("stopped");
        setBouncing(true);
        setTimeout(() => setBouncing(false), 520);
      }, 1060),
      /* 1 760  → message + tip fade in */
      setTimeout(() => { setShowMessage(true); setTipVisible(true); }, 1760),
      /* 3 060  → scooter exits right (1 s transit) */
      setTimeout(() => setPhase("driving-out"), 3060),
      /* 4 060  → off-screen; pause 1.5 s then start the next loop */
      setTimeout(() => {
        setPhase("done");
        setTimeout(() => setCycleKey((k) => k + 1), 1500);
      }, 4060),
    ];
    return () => timers.forEach(clearTimeout);
  }, [cycleKey]);  /* re-runs every cycle */

  /* Tip rotation — starts when message first appears, loops forever */
  useEffect(() => {
    if (!showMessage) return;
    const id = setInterval(() => {
      setTipVisible(false);
      setTimeout(() => {
        setTipIdx((i) => (i + 1) % GROCERY_TIPS.length);
        setTipVisible(true);
      }, 380);
    }, 4500);
    return () => clearInterval(id);
  }, [showMessage]);

  const isMoving = phase === "driving-in" || phase === "driving-out";
  const tip      = GROCERY_TIPS[tipIdx];

  /* Scooter position + transition — controlled via inline style */
  const scooterLeft =
    phase === "pre"                              ? "-220px"
    : phase === "driving-out" || phase === "done" ? "calc(100% + 20px)"
    : "calc(50% - 100px)";                 /* centred: ½ container − ½ scooter (200px wide) */
  const scooterTransition =
    phase === "driving-in"  ? "left 1s cubic-bezier(0.22, 1, 0.36, 1)"
    : phase === "driving-out" ? "left 1s cubic-bezier(0.64, 0, 0.78, 0)"
    : "none";

  return (
    <div className="gc-empty-root">

      {/* ── Full-width transit lane — outside gc-empty-inner so it spans the whole panel ── */}
      <div className="gc-transit-lane">

        {/* Speed streaks — shown while scooter is moving */}
        {isMoving && (
          <div className="gc-speed-wrap">
            <span className="gc-sl gc-sl--1" />
            <span className="gc-sl gc-sl--2" />
            <span className="gc-sl gc-sl--3" />
            <span className="gc-sl gc-sl--4" />
            <span className="gc-sl gc-sl--5" />
          </div>
        )}

        {/* Ground shadow */}
        <div className="gc-ground" />

        {/* ── Scooter — translates across the lane ── */}
        <div
          className="gc-scooter-outer"
          style={{ left: scooterLeft, transition: scooterTransition }}
        >
          <img
            src={PalletScooter}
            alt="Pallet delivery scooter"
            className={`gc-scooter-img${bouncing ? " gc-scooter-img--bounce" : ""}`}
            draggable={false}
          />
        </div>

      </div>{/* end gc-transit-lane */}

      {/* ── Message + tip: padded inner area, centred ── */}
      <div className="gc-empty-inner">
        <div className={`gc-empty-text${showMessage ? " gc-empty-text--in" : ""}`}>
          <h3 className="gc-empty-title">Your cart is empty</h3>
          <p className="gc-empty-sub">Scan a barcode or search for items to get started</p>
        </div>

        {showMessage && (
          <div className="gc-tip-card" style={{ opacity: tipVisible ? 1 : 0 }}>
            <span className="gc-tip-icon" role="img" aria-hidden="true">{tip.icon}</span>
            <p className="gc-tip-text">{tip.text}</p>
          </div>
        )}

        {/* ── Sponsored-by ad block ── */}
        <div className="gc-sponsor">
          <span className="gc-sponsor-label">Sponsored by</span>
          <img src={AachiLogo} alt="Aachi" className="gc-sponsor-logo" />
        </div>
      </div>

    </div>
  );
};

const newSwal = Swal.mixin({
  customClass: {
    confirmButton: "button button-success",
    cancelButton: "button button-error",
    container: "close-session-swal-container",
  },
  buttonsStyling: false,
});

const TwoWayCartKeyboard = ({
  managerDetails,
  cartItems,
  handleNewCart,
  customerMobile,
  storeMode,
  machinenewId,
  openDelete,
  setOpenDelete,
  deleteLoader,
  cartActive,
}) => {
  const showSnackbar = useSnackbar();
  const {
    setCartItems,
    setCgst,
    setSgst,
    setTotal,
    setSubTotal,
    setCartId,
    setCartTotalQty,
    total,
    setCess,
  } = useContext(UserContext);
  const cartId = localStorage.getItem("CartId");
  const retailUserDetails = JSON.parse(localStorage.getItem("retailUserDetails"));
  const userUidx = retailUserDetails?.uidx;
  const userName = localStorage.getItem("user_name");
  const amtFocus = useSelector((state) => state.pagination.enterAmtFocus);
  const barcodeLoading = useSelector(getBarcodeStat);
  const orderMetricDataFullState = useSelector(getFullState);
  const scanCheck = useSelector((state) => state.pagination.scan_end);
  const dispatch = useDispatch();
  const { color, setColor } = useContext(UserContext);
  const isMobile = useGetDevice();

  const [isFlushing, setIsFlushing] = useState(false);
  const [managerPin, setManagerPin] = useState("");
  const [managerNumber, setManagerNumber] = useState("");
  const [isVisible, setVisible] = useState(false);
  const [currentIndexBox, setcurrentIndexBox] = useState(false);
  const [currentRow, setCurrentRow] = useState(0);

  const [drawerError, setDrawerError] = useState(false);

  const [dropdownOpen, setDropdownOpen] = useState(false);

  const deleteKeyPressedRef = useRef(false);

  let startRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (
        !e.target.closest(".custom-select-trigger") &&
        !e.target.closest(".custom-select-options")
      ) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Whenever focus leaves the cart table (click inside on a non-input area,
  // click outside, modal open, etc.) reset the keyboard cursor back to <thead>
  // so the next ArrowDown always restarts highlight from row 1.
  useEffect(() => {
    const handleFocusOut = (e) => {
      const tableEl = document.getElementById("main-table-wrapper");
      if (!tableEl) return;

      // Only care when the element losing focus is inside the table
      if (!tableEl?.contains(e.target)) return;

      const relatedTarget = e.relatedTarget;
      // If focus is moving somewhere still inside the table, leave the cursor alone
      if (relatedTarget && tableEl?.contains(relatedTarget)) return;

      // Focus left the table — clear inline highlight and reset cursor to header
      if (startRef.current) {
        startRef.current.style.backgroundColor = "";
        startRef.current.style.color = "";
      }
      const theadEl = tableEl.querySelector("thead");
      if (theadEl) {
        startRef.current = theadEl;
      }
    };

    document.addEventListener("focusout", handleFocusOut);
    return () => document.removeEventListener("focusout", handleFocusOut);
  }, []);

  // Park the keyboard cursor on <thead> at mount. No highlight here — the header
  // is just an anchor, not a selectable row; changeStyle paints green from the
  // first ArrowDown onward. (Highlighting it made the header show up green
  // whenever the page remounted with items already in the cart.)
  useEffect(() => {
    startRef.current?.focus();
  }, []);

  const changeStyle = (current, sibling) => {
    if (current === sibling) {
      const inputElement = sibling.children?.[0].children?.[2]?.children?.[0]?.children?.[0];
      inputElement?.focus();
      sibling.style.backgroundColor = "#7ed56f";
      sibling.style.color = "#000";
      startRef.current = sibling;
    } else if (current && sibling && current !== sibling) {
      current.style.backgroundColor = "";
      current.style.color = "";
      current.blur(); // Remove focus from the current element
      sibling.focus();
      sibling.style.backgroundColor = "#7ed56f";
      sibling.style.color = "#000";
      startRef.current = sibling;
    }
  };
  //toggle password visibility
  const toggle = () => {
    setVisible(!isVisible);
  };

  const locId = localStorage.getItem("locId");

  const handleDeleteRow = (
    gtin,
    sp,
    mrp,
    batchNo,
    purchasePrice,
    itemQuantity
    // currentIndex,
    // siblings,
    // parent
  ) => {
    const payload = {
      cartId: cartId,
      sellingPrice: sp,
      gtin: gtin,
      locationId: locId,
      mrp: mrp,
      inventoryChecks: "NO",
      batchNo: batchNo,
      purchasePrice: purchasePrice || "",
    };
    deleteItemThroughScanner(payload)
      .then((res) => {
        const metaData = {
          itemId: payload?.gtin,
          quantity: itemQuantity,
          sellingPrice: payload?.sellingPrice,
          cartId: payload?.cartId,
          mrp: payload?.mrp,
        };
        eventTracker({ metaData, eventType: eventTrackerConstant.CART_ITEM_REMOVED });
        // document.getElementById("hiddenBtn").focus();
        showSnackbar("Success item removed", "success");
        setCartItems(
          (res?.data?.data?.cartProducts || []).sort((a, b) =>
            b?.modifiedDate.localeCompare(a?.modifiedDate)
          )
        );
        setCartTotalQty(res?.data?.data?.billing?.quantity);
        setCgst(res?.data?.data?.billing?.cgst);
        setSgst(res?.data?.data?.billing?.sgst);
        setCess(res?.data?.data?.billing?.totalCessAmount);
        setTotal(res?.data?.data?.billing?.totalCartValue);
        setSubTotal(res?.data?.data?.billing?.subtotal);
        const nextCoupon = res?.data?.data?.cartCoupon || null;
        if (nextCoupon?.couponValue) {
          dispatch(addcoupon(nextCoupon));
          localStorage.setItem("cartCoupon", JSON.stringify(nextCoupon));
        } else {
          const storedCoupon = localStorage.getItem("cartCoupon");
          const wasCouponApplied =
            !!storedCoupon && storedCoupon !== "undefined" && storedCoupon !== "null";
          dispatch(addcoupon(null));
          localStorage.removeItem("cartCoupon");
          if (wasCouponApplied) {
            showSnackbar("Coupon got removed", "info");
          }
        }
      })
      .catch((err) => {
        showSnackbar(err?.response?.data?.message || err?.message, "error");
      });
  };

  // flush cart
  const handleFlush = async (e) => {
    e.preventDefault();
    setDrawerError(false);
    setIsFlushing(true);
    let managerpayload = {
      mobile: managerNumber,
      primaryPassword: managerPin,
    };
    try {
      const res = await validateManagerPin(managerpayload);
      if (res?.data?.data?.status === "ERROR") {
        throw new Error(res?.data?.data?.message);
      }
    } catch (err) {
      showSnackbar(err?.response?.data?.message || err?.message, "error");
      setDrawerError(true);
      setIsFlushing(false);
      return;
    }

    try {
      const newFlushPayload = {
        cartId: cartId,
        updatedBy: userUidx,
        updatedByName: userName,
      };
      await flushCartNew(newFlushPayload);
      const res = await dataRetriveWhenPageRefresh(cartId);
      showSnackbar("Success cart items deleted", "success");
      const currentStoreMode = localStorage.getItem("storeMode") || "ONLINE";
      localStorage.setItem("cartStoreMode", currentStoreMode);
      const totalItems = cartItems.reduce(
        (totalQuantity, item) => totalQuantity + item.quantity,
        0
      );
      const metaData = {
        cartId: cartId,
        itemsCount: totalItems,
        amount: total,
      };
      eventTracker({ metaData, eventType: eventTrackerConstant.CART_DELETED });
      dispatch(resetStateWithoutTime(0));
      const backupData = {
        ...orderMetricDataFullState,
        manuallyAdded: 0,
        scannedProduct: 0,
        isMultipleBatch: false,
        avgProductAddTime: "",
        totalProducts: 0,
        productTimeArray: [],
        editOrderItem: 0,
        manuallyRemovedItems: 0,
        manualItemsGtin: [],
      };
      localStorage.setItem("orderMetricData", JSON.stringify(backupData));
      setCartId(res?.data?.data?.data?.cartId);
      setCartItems(res?.data?.data?.data?.cartProducts || []);
      setCartTotalQty(res?.data?.data?.data?.billing?.quantity);
      setCgst(res?.data?.data?.data?.billing?.cgst);
      setSgst(res?.data?.data?.data?.billing?.sgst);
      setCess(res?.data?.data?.data?.billing?.totalCessAmount);
      setSubTotal(res?.data?.data?.data?.billing?.subtotal);
      setTotal(res?.data?.data?.data?.billing?.totalCartValue);

      dispatch(addcoupon(null));
      localStorage.removeItem("cartCoupon");
      newSwal.fire({
        title: `Cart Deleted Successfully`,
        icon: "success",
        didOpen: () => {
          newSwal.getConfirmButton().focus();
        },
        focusConfirm: true,
      });
    } catch (err) {
      showSnackbar(err?.response?.data?.message || err?.message, "error");
      newSwal.fire({
        title: `Cart Deletion Failed`,
        icon: "error",
        text: `${err?.response?.data?.message || err?.message}`,
        didOpen: () => {
          newSwal.getConfirmButton().focus();
        },
      });
    } finally {
      setOpenDelete(false);
      setIsFlushing(false);
    }
  };

  // const [isParent, setIsParent] = useState();
  const [siblingsArray, setIsSiblingsArray] = useState(null);

  const checkKey = (e) => {
    e = e || window.e;
    let start = startRef.current;
    const parent = start?.parentElement;
    let siblings = Array.from(parent?.children || []);
    let currentIndex = siblings.findIndex((cell) => {
      return cell === start;
    });

    // Stale ref: startRef points to a detached node — re-anchor to live <thead>
    if (currentIndex === -1 || parent === null) {
      const tableEl = document.getElementById("main-table-wrapper");
      const theadEl = tableEl?.querySelector("thead");
      if (!theadEl) return;
      startRef.current = theadEl;
      start = theadEl;
      siblings = Array.from(theadEl.parentElement?.children || []);
      currentIndex = 0;
    }

    // Valid ref but the row lost its highlight — reset to header so ArrowDown starts from row 1
    if (currentIndex > 0 && !start.contains(document.activeElement)) {
      start = siblings[0];
      startRef.current = start;
      currentIndex = 0;
    }

    if (siblings?.length > 0) {
      setIsSiblingsArray(siblings);
    }

    if (start && currentIndex !== -1) {
      if (e.ctrlKey && e.key === "ArrowRight") {
        // Check for Ctrl+Right
        e.preventDefault();
        const start = startRef.current;
        const spInput = start?.querySelector(".sp-input");
        if (spInput) {
          spInput.focus();
        }
      } else if (e.ctrlKey && e.key === "ArrowLeft") {
        // Check for Ctrl+Left
        e.preventDefault();
        const start = startRef.current;
        const qtyInput = start?.querySelector(".qty-input");
        if (qtyInput) {
          qtyInput.focus();
        }
      } else if (!e.ctrlKey && e.key === "ArrowRight") {
        // Plain ArrowRight → increment qty of current highlighted row
        e.preventDefault();
        const plusBtn = startRef.current?.querySelector(".qty-stepper-btn--plus");
        if (plusBtn && !plusBtn.disabled) {
          plusBtn.click();
        }
      } else if (!e.ctrlKey && e.key === "ArrowLeft") {
        // Plain ArrowLeft → decrement qty of current highlighted row
        e.preventDefault();
        const minusBtn = startRef.current?.querySelector(".qty-stepper-btn--minus");
        if (minusBtn && !minusBtn.disabled) {
          minusBtn.click();
        }
      } else if (e.key === "ArrowDown") {
        e.preventDefault();
        setcurrentIndexBox(true);
        let nextIndex = currentIndex + 1;
        if (currentIndex === siblings.length - 1) {
          nextIndex = currentIndex;
        }
        const sibling = siblings[nextIndex];
        changeStyle(start, sibling);
        setCurrentRow(nextIndex);
        // Focus directly so the input is always focused even when currentRow value doesn't change
        requestAnimationFrame(() => {
          startRef.current?.querySelector("input")?.focus();
        });
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setcurrentIndexBox(true);
        let previousIndex = currentIndex - 1;
        if (previousIndex < 1) {
          previousIndex = 1;
        }
        const sibling = siblings[previousIndex];
        changeStyle(start, sibling);
        setCurrentRow(previousIndex);
        requestAnimationFrame(() => {
          startRef.current?.querySelector("input")?.focus();
        });
      } else if (e.key === "Delete") {
        e.preventDefault();
        if (deleteKeyPressedRef.current) return;
        deleteKeyPressedRef.current = true;
        setcurrentIndexBox(false);
        const focusedRowGtin = cartItems[currentIndex - 1]?.gtin;
        const mrp = cartItems[currentIndex - 1]?.mrp;
        const sp = cartItems[currentIndex - 1]?.sellingPrice;
        const batchNo = cartItems[currentIndex - 1]?.batchNo;
        const purchasePrice = cartItems[currentIndex - 1]?.purchasePrice;
        const itemQuantity = cartItems[currentIndex - 1]?.quantity;
        handleDeleteRow(focusedRowGtin, sp, mrp, batchNo, purchasePrice, itemQuantity);
      }
    }
  };

  useEffect(() => {
    const handleKeyUp = (e) => {
      if (e.key === "Delete") {
        deleteKeyPressedRef.current = false;
      }
    };
    document.addEventListener("keyup", handleKeyUp);
    return () => document.removeEventListener("keyup", handleKeyUp);
  }, []);

  //to directly focus on the input of currentIndex which is active:
  useEffect(() => {
    // Function to focus on the currentIndex input without any keydown
    const focusOnCurrentInput = () => {
      const start = startRef.current;
      const inputElement = start?.querySelector("input");
      if (inputElement) {
        inputElement.focus();
      }
    };
    focusOnCurrentInput();
  }, [currentRow]);

  const style = {
    backgroundColor: color === "dark" ? "#353a50" : "#fff",
    color: color === "dark" ? "white" : "black",
    padding: isMobile ? "5%" : "2%",
    borderRadius: "10px",
    width: isMobile ? "100%" : "50%",
    maxWidth: "100vw",
    maxHeight: "100%",
    position: "fixed",
    top: "50%",
    left: "0",
    transform: `translate(${isMobile ? `0%` : `50%`}, -50%)`,
    overflowY: "auto",
  };

  useEffect(() => {
    if (cartActive) {
      document.addEventListener("keydown", checkKey);
    }
    return () => {
      document.removeEventListener("keydown", checkKey);
    };
  }, [cartActive, cartItems]);

  //scroll functionality to top
  var myDiv = document.getElementById("move-to-top-wrapper");
  function captureScrollPosition() {
    var scrollPosition = myDiv.scrollTop;
  }
  myDiv?.addEventListener("scroll", captureScrollPosition);

  const handleClose = () => {
    setOpenDelete(false);
    setDrawerError(false);
    setManagerPin("");
    setManagerNumber("");
    setVisible(false);
  };

  const { offerCheckList, setOfferCheckList } = useContext(UserContext);

  // const offerCheck = (id, isShow) => {
  //   cartOfferValidation(id).then((res) => {
  //     if (res?.data?.data?.length === 0) {
  //       setOpenOffer(false);
  //       setOfferCheckList([]);
  //     } else {
  //       if (isShow == "check") {
  //         setOpenOffer(true);
  //       } else {
  //         setOpenOffer(false);
  //       }
  //       setOfferCheckList(res?.data?.data);
  //     }
  //   });
  // };

  const { openOffer, setOpenOffer } = useContext(UserContext);

  const handleCloseOfferModal = () => {
    setOpenOffer(false);
  };

  // useEffect(() => {
  //   offerCheck(cartId, "nocheck");
  // }, [cartItems]);

  return (
    <>
      {offerCheckList?.length === 0 ? null : (
        <Modal
          open={openOffer}
          onClose={handleCloseOfferModal}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
        >
          <Box
            sx={{
              backgroundColor: color === "dark" ? "#353a50" : "#fff",
              color: color === "dark" ? "white" : "black",
              position: isMobile ? "static" : "relative",
              width: isMobile ? "100%" : "35%",
              maxWidth: "40vw",
              maxHeight: isMobile ? "100%" : "90%",
              transform: isMobile ? "none" : "translate(50%, -50%)",
              borderRadius: "10px",
              padding: "3vh",
              top: "50%",
              left: "10%",
              overflowY: "auto",
              ...(isMobile && {
                height: "100%",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
              }),
            }}
          >
            <Typography variant="h5">Available offers in your cart</Typography>
            {offerCheckList?.map((e, i) => (
              <Card className="offer-avail-card" key={i}>
                <p style={{ fontWeight: "bold", paddingLeft: "20px", paddingTop: "20px" }}>
                  {e?.mainProductData?.mrp >= e?.mainProductData?.sellingPrice ? (
                    <b className="strike-off-mrp">&nbsp;₹{e?.mainProductData?.mrp}&nbsp;</b>
                  ) : null}
                  <b>&nbsp;&nbsp;</b>₹{e?.mainProductData?.sellingPrice}&nbsp;&nbsp; Buy{" "}
                  {e?.mainProductData?.buyQuantity} Get {e?.offerProductData?.getQuantity} free
                </p>
                <div className="available-offer-box">
                  <div>
                    <label>
                      <p className="select-item-details">
                        Product : {e?.mainProductData?.productName}{" "}
                      </p>
                      <p className="select-item-details">Gtin : {e?.mainProductData?.gtin} </p>
                      <p className="select-item-details">Batch : {e?.mainProductData?.batchNo} </p>
                    </label>
                  </div>
                  <div>
                    <label>
                      <p className="select-item-details">
                        Offer Product : {e?.offerProductData?.productName}{" "}
                      </p>
                      <p className="select-item-details">Gtin : {e?.mainProductData?.gtin} </p>
                      <p className="select-item-details">Batch : {e?.mainProductData?.batchNo} </p>
                    </label>
                  </div>
                </div>
              </Card>
            ))}
          </Box>
        </Modal>
      )}

      {/* <Modal
        open={openDelete}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={style}>
          <form onSubmit={handleFlush}>
            <Typography variant="h5" fontSize="15px" fontWeight="bold">
              Select manager
            </Typography>

            <StyledSelect
              autoFocus
              onChange={(e) => setManagerNumber(e.value)}
              options={managerDetails}
            />

            <FormControl sx={{ width: "100%", marginTop: "10px" }} variant="outlined">
              <Input
                id="outlined-adornment-password"
                type={isVisible ? "text" : "password"}
                onChange={(e) => setManagerPin(e.target.value)}
                className="numberInput1"
                inputProps={{ inputMode: "numeric" }}
                autoComplete="new-password"
                placeholder="Pin"
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton aria-label="toggle password visibility" onClick={toggle} edge="end">
                      {isVisible ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                }
                label="Pin"
                disableUnderline
              />
            </FormControl>
            <Box className="cancel-wrapper-box">
              <StyledButton type="button" onClick={handleClose}>
                Cancel
              </StyledButton>
              <StyledButton
                disabled={managerPin.length < 4 || !managerNumber}
                onClick={handleFlush}
                loading={isFlushing}
                type="submit"
              >
                proceed
              </StyledButton>
            </Box>
          </form>
        </Box>
      </Modal> */}

      {/* REMOVE the entire `const style = { ... }` object */}

      {/* REPLACE the <Modal open={openDelete}> block with: */}
      <>
        {openDelete && <div className="clear-cart-overlay" onClick={handleClose} />}
        <aside className={`clear-cart-drawer ${openDelete ? "clear-cart-drawer--open" : ""}`}>
          {/* Header */}
          <div className="drawer-header">
            <h1 className="drawer-title">Clear Cart</h1>
            <button className="drawer-close-btn" onClick={handleClose}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M18 6 6 18" />
                <path d="m6 6 12 12" />
              </svg>
            </button>
          </div>

          {/* Body */}
          <div className="drawer-body">
            <form onSubmit={handleFlush}>
              {/* Icon */}
              <div className="drawer-icon-wrap">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="40"
                  height="40"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="drawer-icon-svg"
                >
                  <circle cx="12" cy="12" r="10" />
                  <path d="m15 9-6 6" />
                  <path d="m9 9 6 6" />
                </svg>
              </div>

              {/* Title + desc */}
              <div className="drawer-heading-wrap">
                <h3 className="drawer-heading">Clear current cart?</h3>
                <p className="drawer-subtext">
                  This action needs manager approval before all items are removed from the active
                  cart.
                </p>
              </div>

              {/* Form card */}
              <div className="drawer-form-card">
                {/* <div className="drawer-field">
                  <label className="drawer-label">Manager name</label>
                  <StyledSelect
                    autoFocus
                    onChange={(e) => setManagerNumber(e.value)}
                    options={managerDetails}
                    className="drawer-select"
                  />
                </div> */}

                <div className="drawer-field" style={{ position: "relative" }}>
                  <label className="drawer-label">Manager name</label>
                  <div
                    className={`custom-select-trigger ${dropdownOpen ? "open" : ""}`}
                    onClick={() => setDropdownOpen(!dropdownOpen)}
                  >
                    <span className={!managerNumber ? "placeholder" : ""}>
                      {managerNumber
                        ? managerDetails?.find((m) => m?.value === managerNumber)?.label
                        : "Select manager"}
                    </span>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#94a3b8"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="m6 9 6 6 6-6" />
                    </svg>
                  </div>

                  {dropdownOpen && (
                    <ul className="custom-select-options">
                      {managerDetails?.map((m, i) => (
                        <li
                          key={i}
                          className={`custom-option ${
                            managerNumber === m?.value ? "selected" : ""
                          }`}
                          onClick={() => {
                            setManagerNumber(m?.value);
                            setDropdownOpen(false);
                          }}
                        >
                          {m?.label}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                <div className="drawer-field">
                  <label className="drawer-label">Manager PIN</label>
                  <div className="drawer-pin-wrap">
                    <input
                      type={isVisible ? "text" : "password"}
                      onChange={(e) => setManagerPin(e.target.value)}
                      inputMode="numeric"
                      placeholder="Enter 4-digit PIN"
                      autoComplete="new-password"
                      className="drawer-pin-input"
                      value={managerPin}
                    />
                    <button type="button" className="drawer-pin-toggle" onClick={toggle}>
                      {isVisible ? (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="18"
                          height="18"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
                          <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
                          <line x1="1" y1="1" x2="23" y2="23" />
                        </svg>
                      ) : (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="18"
                          height="18"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                          <circle cx="12" cy="12" r="3" />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>

                {/* Approver card — shown when manager is selected */}
                {managerNumber && (
                  <div className="drawer-approver-card">
                    <p className="drawer-approver-name">
                      Approver:{" "}
                      {managerDetails?.find((m) => m?.value === managerNumber)?.label ||
                        managerNumber}
                    </p>
                    <p className="drawer-approver-role">
                      {managerDetails?.find((m) => m?.value === managerNumber)?.role || ""}
                    </p>
                  </div>
                )}

                <p className="drawer-helper-text">
                  Select a manager and enter their PIN to unlock cart clearance.
                </p>
              </div>

              {/* Error state */}
              {drawerError && (
                <div className="drawer-error-banner">Incorrect manager PIN. Try again.</div>
              )}

              {/* Actions */}
              <div className="drawer-actions">
                <button
                  type="button"
                  className="drawer-btn drawer-btn--cancel"
                  onClick={handleClose}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="drawer-btn drawer-btn--confirm"
                  disabled={managerPin?.length < 4 || !managerNumber || isFlushing}
                  onClick={handleFlush}
                >
                  {isFlushing ? "Clearing..." : "Clear Cart"}
                </button>
              </div>
            </form>
          </div>
        </aside>
      </>

      {barcodeLoading && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            padding: "6px 14px",
            backgroundColor: "rgba(5, 98, 251, 0.06)",
            borderBottom: "1px solid rgba(5, 98, 251, 0.2)",
          }}
        >
          <CircularProgress size="1rem" style={{ color: "#0562FB" }} />
          <span
            style={{ fontSize: "13px", fontWeight: 600, color: "#0562FB", whiteSpace: "nowrap" }}
          >
            Scanning product…
          </span>
          <Box sx={{ flexGrow: 1 }}>
            <LinearProgress />
          </Box>
        </div>
      )}

      {cartItems?.length === 0 ? (
        <GroceryEmptyCart />
      ) : (
        <div className="table-wapper-modal-box-keyboard" id="move-to-top-wrapper">
          <table id="main-table-wrapper">
            <thead ref={startRef}>
              <tr className="table-tr-wrap-box">
                <th className="table-th-wrapper-text">TYPE</th>
                <th className="table-th-wrapper-text">S.NO</th>
                <th className="table-th-wrapper-text item-name-grocery">ITEM NAME</th>
                <th className="table-th-wrapper-text">QTY</th>
                <th className="table-th-wrapper-text">MRP</th>
                <th className="table-th-wrapper-text">SP</th>
                <th className="table-th-wrapper-text">HSN</th>
                <th className="table-th-wrapper-text">GST%</th>
                <th className="table-th-wrapper-text">SGST</th>
                <th className="table-th-wrapper-text">CGST</th>
                <th className="table-th-wrapper-text">Total</th>
              </tr>
            </thead>
            {(() => {
              /* ── Group items by [BundleName] prefix in variantName ── */
              const BUNDLE_RE = /^\[(.+?)\]/;
              const groups = [];
              let globalIdx = 0;
              let i = 0;
              while (i < (cartItems?.length || 0)) {
                const item = cartItems[i];
                const match = item?.variantName?.match(BUNDLE_RE);
                if (match) {
                  const bundleName = match[1];
                  const groupItems = [];
                  while (
                    i < cartItems.length &&
                    cartItems[i]?.variantName?.match(BUNDLE_RE)?.[1] === bundleName
                  ) {
                    groupItems.push({ item: cartItems[i], idx: globalIdx++ });
                    i++;
                  }
                  groups.push({ type: "bundle", bundleName, groupItems });
                } else {
                  groups.push({ type: "item", item, idx: globalIdx++ });
                  i++;
                }
              }

              return groups.map((group, gi) => {
                if (group.type === "bundle") {
                  const totalSP = group.groupItems.reduce(
                    (s, { item }) => s + (parseFloat(item?.sellingPrice) || 0),
                    0
                  );
                  return (
                    <>
                      {/* Bundle group header row */}
                      <tbody key={`bh-${gi}`} className="cart-bundle-group-header-tbody">
                        <tr className="cart-bundle-group-header-row">
                          <td colSpan={11}>
                            <div className="cart-bundle-group-header-inner">
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="14"
                                height="14"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="#2a71ff"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                aria-hidden="true"
                              >
                                <path d="M2.97 12.92A2 2 0 0 0 2 14.63v3.24a2 2 0 0 0 .97 1.71l3 1.8a2 2 0 0 0 2.06 0L12 19v-5.5l-5-3-4.03 2.42Z" />
                                <path d="m7 16.5-4.74-2.85" />
                                <path d="m7 16.5 5-3" />
                                <path d="M7 16.5v5.17" />
                                <path d="M12 13.5V19l3.97 2.38a2 2 0 0 0 2.06 0l3-1.8a2 2 0 0 0 .97-1.71v-3.24a2 2 0 0 0-.97-1.71L17 10.5l-5 3Z" />
                                <path d="m17 16.5-5-3" />
                                <path d="m17 16.5 4.74-2.85" />
                                <path d="M17 16.5v5.17" />
                                <path d="M7.97 4.42A2 2 0 0 0 7 6.13v4.37l5 3 5-3V6.13a2 2 0 0 0-.97-1.71l-3-1.8a2 2 0 0 0-2.06 0l-3 1.8Z" />
                                <path d="M12 8 7.26 5.15" />
                                <path d="m12 8 4.74-2.85" />
                                <path d="M12 13.5V8" />
                              </svg>
                              <span className="cart-bundle-group-name">{group.bundleName}</span>
                              <span className="cart-bundle-group-badge">
                                {group.groupItems.length} item{group.groupItems.length !== 1 ? "s" : ""}
                              </span>
                              <span className="cart-bundle-group-sp">
                                ₹{totalSP.toFixed(2)}
                              </span>
                            </div>
                          </td>
                        </tr>
                      </tbody>
                      {/* Bundle child rows */}
                      {group.groupItems.map(({ item, idx }) => (
                        <CartItemTable
                          key={idx}
                          e={item}
                          i={idx}
                          changeStyle={changeStyle}
                          setCurrentRow={setCurrentRow}
                          currentIndexBox={currentIndexBox}
                          setcurrentIndexBox={setcurrentIndexBox}
                        />
                      ))}
                    </>
                  );
                }
                return (
                  <CartItemTable
                    key={group.idx}
                    e={group.item}
                    i={group.idx}
                    changeStyle={changeStyle}
                    setCurrentRow={setCurrentRow}
                    currentIndexBox={currentIndexBox}
                    setcurrentIndexBox={setcurrentIndexBox}
                  />
                );
              });
            })()}
          </table>
        </div>
      )}
    </>
  );
};

export default memo(TwoWayCartKeyboard);
